
import React, { useState } from 'react';
import { Story } from '../types';
import Button from './Button';

interface LibraryViewProps {
  stories: any[];
  onSelect: (story: any) => void;
  onDelete: (id: string) => void;
  onClearAll: () => void;
  onBack: () => void;
}

const LibraryView: React.FC<LibraryViewProps> = ({ stories, onSelect, onDelete, onClearAll, onBack }) => {
  const [imgErrors, setImgErrors] = useState<Record<string, boolean>>({});

  return (
    <div className="max-w-7xl mx-auto px-6 py-12 space-y-10 animate-fadeIn">
      <div className="flex flex-col md:flex-row justify-between items-center gap-4">
        <div className="space-y-1 text-center md:text-left">
          <h2 className="text-4xl font-bold text-indigo-900">Ma Bibliothèque</h2>
          <p className="text-indigo-400">Retrouvez toutes vos aventures magiques en Tifinagh.</p>
        </div>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={onBack} className="!py-2 !px-4 !text-xs">
            Générer nouveau
          </Button>
        </div>
      </div>

      {stories.length === 0 ? (
        <div className="text-center py-20 bg-white/50 rounded-[2rem] border-2 border-dashed border-indigo-100">
          <div className="text-6xl mb-4">📚</div>
          <p className="text-indigo-300 text-lg">Votre bibliothèque est vide.</p>
          <Button variant="primary" onClick={onBack} className="mt-6 mx-auto">
            Créer ma première histoire
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {stories.map((story) => (
            <div 
              key={story.id} 
              className="bg-white rounded-2xl shadow-sm overflow-hidden border border-indigo-50 group hover:scale-[1.02] transition-transform duration-300 h-full flex flex-col"
            >
              <div className="relative aspect-video bg-indigo-50 flex items-center justify-center overflow-hidden">
                {story.coverImage && !imgErrors[story.id] ? (
                  <img 
                    src={story.coverImage} 
                    alt={story.title} 
                    className="w-full h-full object-cover group-hover:scale-110 transition-all duration-700"
                    loading="lazy"
                    onError={() => setImgErrors(p => ({...p, [story.id]: true}))}
                  />
                ) : (
                  <div className="flex flex-col items-center gap-1 opacity-20 select-none bg-indigo-100 w-full h-full justify-center">
                    <span className="text-5xl font-tifinagh text-indigo-300">ⵣ</span>
                    <span className="text-[8px] font-black uppercase text-indigo-400">Image non chargée</span>
                  </div>
                )}
                <div className="absolute top-4 right-4 flex gap-2">
                   <button 
                    onClick={(e) => { e.stopPropagation(); onDelete(story.id!); }}
                    className="bg-white/90 p-2 rounded-full text-red-500 hover:bg-red-500 hover:text-white shadow-sm transition-colors z-10"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
                  </button>
                </div>
              </div>
              <div className="p-5 space-y-4 flex-1 flex flex-col">
                <h3 className="text-lg font-bold text-indigo-900 line-clamp-1">{story.title}</h3>
                <div className="flex justify-between items-center text-[10px] text-indigo-300 font-bold uppercase tracking-widest mt-auto">
                   <span>Magie Amazighe</span>
                   <span>{new Date(story.createdAt).toLocaleDateString()}</span>
                </div>
                <Button 
                  className="w-full !py-2.5 !text-xs" 
                  variant="primary"
                  onClick={() => onSelect(story.id)}
                >
                  Lire l'histoire
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LibraryView;
